/* eslint-disable @typescript-eslint/no-explicit-any */
import { getTxHistory } from '@api/payments';
import careerMosaicUnicentre from '@assets/branding/career-mosaic-unicentre.png';
import trentialWhiteLogo from '@assets/branding/trential-white-logo.svg';
import rectanglePatternImage from '@assets/layout/rectangle-pattern.png';
import IconBreadcrumbs from '@components/breadcrumbs';
import CircularProgressLabel from '@components/circular-progress-label';
import TopBackBtnWrapper from '@components/ui/top-back-btn-wrapper';
import { tawkToConfig } from '@config/addn-configs';
import { TxnState } from '@global/constants/verification';
import { convertToTitleCase, emailTemplate, isClientCareerMosaic } from '@global/helpers/general';
import { Home } from '@mui/icons-material';
import { Alert, Box, Grid, Link, Skeleton, Typography } from '@mui/material';
import { navigateTo } from '@services/navigation';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Closed from './alerts/closed';
import InProgress from './alerts/in-progress';
import Verified from './alerts/verified';
import FormBottomRow from './bottom-row';
import StartProcess from './dialogs/start-process';
import styles from './styles';
import { StartingProcessDialogType } from './types';
import VerificationTiles from './verification-tiles';

function Form() {
  const formData = useSelector((s: any) => s.formData);
  const { orgInfo = {}, txnInfo = {}, formId } = formData ?? {};
  const [loadingOrgImg, setLoadingOrgImg] = useState(true);
  const [payments, setPayments] = useState<any>([]);
  const [openStartingProcessDialog, setOpenStartingProcessDialog] =
    useState<StartingProcessDialogType>(null);

  useEffect(() => {
    if (orgInfo?.orgId && formId) {
      getTxHistory({
        orgId: orgInfo?.orgId,
        formId
      })
        .then((res) => {
          if (res.data) {
            const areThereAnyTxns = res.data?.findIndex((x: any) => x?.paymentList?.length > 0);
            if (areThereAnyTxns >= 0) {
              setPayments(res.data?.[areThereAnyTxns]);
            }
          }
        })
        .catch(() => {});
    }
  }, []);

  // useEffect(() => {
  //   if (txnInfo?.state === TxnState.NOT_INITIALIZED && txnInfo?.questionnaire?.isRequired) {
  //     setOpenStartingProcessDialog({
  //       callback: async () => {}
  //     });
  //   }
  // }, [txnInfo]);

  const contactUs = () => {
    if (tawkToConfig()) {
      window?.Tawk_API?.maximize();
    } else {
      window.open(emailTemplate(import.meta.env.VITE_ORG_MAIL), '_blank');
    }
  };

  const showFormAlert = useMemo(() => {
    const completed =
      TxnState.COMPLETED === txnInfo?.state && txnInfo?.filtered?.remaining?.length <= 0;
    const inProgress =
      [TxnState.IN_PROGRESS, TxnState.DATA_SAVED].includes(txnInfo.state) &&
      txnInfo?.expectedCompletionDate;
    const closed = TxnState.CLOSED === txnInfo?.state && txnInfo?.updatedAt;
    return completed || inProgress || closed;
  }, [txnInfo]);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        backgroundColor: 'bg.light',
        overflowY: 'auto',
        display: 'flex',
        flexFlow: 'column'
      }}>
      {!isClientCareerMosaic() && txnInfo?.form?.name && (
        <TopBackBtnWrapper
          wrapperSx={{ p: '0', m: '0 1.5rem 2rem 1.5rem' }}
          onBackClick={() => navigateTo('/')}
          title={txnInfo?.form?.name}
        />
      )}
      {!showFormAlert && (
        <Box
          sx={{
            padding: '2.4rem 1.5rem',
            backgroundColor: 'primary.dark',
            // this is done to remove space of 1px b/w divs which shows up on some screen sizes
            pb: '2px',
            borderRadius: '16px 16px 0 0',
            ...(isClientCareerMosaic()
              ? {
                  backgroundImage: isClientCareerMosaic()
                    ? `url(${rectanglePatternImage})`
                    : 'none',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  backgroundSize: 'cover'
                  // opacity: 0.3
                }
              : {})
          }}>
          <Box sx={{}}>
            {isClientCareerMosaic() ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { sm: 'row', xs: 'column' },
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                <img
                  onLoad={() => setLoadingOrgImg(false)}
                  src={careerMosaicUnicentre}
                  style={{
                    height: '4.1rem',
                    borderRadius: '8px'
                  }}
                  alt="org-image"
                />
                <img
                  onLoad={() => setLoadingOrgImg(false)}
                  src={trentialWhiteLogo}
                  style={{
                    height: '2.1rem',
                    borderRadius: '8px',
                    marginRight: '2rem'
                  }}
                  alt="trential-white-logo"
                />
              </Box>
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: loadingOrgImg ? '5px' : '1rem'
                }}>
                {loadingOrgImg && (
                  <Skeleton
                    variant="rectangular"
                    style={{
                      backgroundColor: '#FFEFDC1A',
                      height: '6.2rem',
                      width: '6.2rem',
                      borderRadius: '8px'
                    }}
                  />
                )}
                <img
                  onLoad={() => setLoadingOrgImg(false)}
                  src={orgInfo?.imageUrl}
                  style={{
                    height: loadingOrgImg ? 0 : '5.1rem',
                    width: loadingOrgImg ? 0 : '5.1rem',
                    borderRadius: '8px'
                  }}
                  alt="org-image"
                />
                <Box sx={{ display: 'flex', flexFlow: 'column', lineHeight: 0, rowGap: '.7rem' }}>
                  <Typography
                    variant="body1"
                    sx={{ color: '#fff', fontWeight: '800', fontFamily: 'Poppins' }}>
                    {orgInfo?.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#fff', fontWeight: '600' }}>
                    is requesting the following verifications
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              ...styles.stepsCompleted,
              m: isClientCareerMosaic() ? '0 0rem' : '0 1.5rem',
              backgroundColor: isClientCareerMosaic() ? 'transaparent' : 'primary.dark'
            }}>
            <Box sx={{ display: 'flex', flexFlow: 'column', rowGap: '.7rem' }}>
              <Typography
                variant="h6"
                sx={{
                  color: isClientCareerMosaic() ? '#000' : '#fff',
                  fontWeight: '700',
                  fontFamily: 'Poppins'
                }}>
                Steps Completed
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: isClientCareerMosaic() ? '#000' : '#fff', fontWeight: '600' }}>
                {(txnInfo?.filtered?.remaining ?? [])?.length > 0
                  ? 'Click the verifications to start the process'
                  : 'All verifications are done'}
              </Typography>
            </Box>
            <Box sx={isClientCareerMosaic() ? { ml: 1, display: 'flex' } : {}}>
              <CircularProgressLabel
                done={
                  Object.keys(txnInfo?.verificationList ?? {})?.length -
                  (txnInfo?.filtered?.remaining ?? [])?.length
                }
                total={Object.keys(txnInfo?.verificationList ?? {})?.length}
              />
            </Box>
          </Box>
        </Box>
      )}

      {TxnState.COMPLETED === txnInfo?.state && txnInfo?.filtered?.remaining?.length <= 0 ? (
        <Verified txnInfo={txnInfo} />
      ) : [TxnState.IN_PROGRESS, TxnState.DATA_SAVED].includes(txnInfo.state) &&
        txnInfo?.expectedCompletionDate ? (
        <InProgress txnInfo={txnInfo} contactUs={contactUs} />
      ) : TxnState.CLOSED === txnInfo?.state && txnInfo?.updatedAt ? (
        <Closed txnInfo={txnInfo} contactUs={contactUs} />
      ) : null}

      <Grid
        container
        sx={{ px: '1.5rem', mt: 2 }}
        alignItems="center"
        justifyContent={isClientCareerMosaic() ? 'space-between' : 'flex-end'}>
        {isClientCareerMosaic() && (
          <IconBreadcrumbs
            list={[{ title: 'Home', link: '/', icon: <Home /> }]}
            currentPage={txnInfo?.form?.name || 'Form'}
          />
        )}

        {payments?.paymentList?.length > 0 && (
          <Link
            variant="body2"
            component={Typography}
            onClick={() => navigateTo('/form-payments')}
            sx={{
              cursor: 'pointer',
              width: 'fit-content',
              fontWeight: 800
            }}>
            View Transactions
          </Link>
        )}
      </Grid>

      {isClientCareerMosaic() && (
        <Alert
          severity="info"
          sx={{
            color: '#073B72',
            backgroundColor: '#E4F1FF',
            fontWeight: '400',
            paddingTop: '8px',
            paddingBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'row',
            mt: 2,
            width: '100%'
          }}>
          You have selected the following service(s):{' '}
          {txnInfo.evaluationInfo
            ?.map((x: { serviceType: string }) => convertToTitleCase(x.serviceType))
            ?.join(', ')}
        </Alert>
      )}

      <VerificationTiles setOpenStartingProcessDialog={setOpenStartingProcessDialog} />
      <FormBottomRow setOpenStartingProcessDialog={setOpenStartingProcessDialog} />
      {openStartingProcessDialog && (
        <StartProcess
          openStartingProcessDialog={openStartingProcessDialog}
          setOpenStartingProcessDialog={setOpenStartingProcessDialog}
        />
      )}
    </Box>
  );
}

export default Form;
